import React from 'react';
import cx from 'classnames';
import { useRouter } from 'next/router';
import { observer } from 'mobx-react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import TextField from '@material-ui/core/TextField';
import Link from 'next/link';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import Checkbox from '@material-ui/core/Checkbox';
import DeleteIcon from '@material-ui/icons/Delete';
import MediaIcon from '../../../components/Media/Icon';
import withStores from '../../../stores/withStores';
import adaTheme from '../../../theme';
import useI18n from '../../../i18n/useI18n';

const useStyles = makeStyles(theme => ({
  tooltip: {
    maxWidth: 350,
    backgroundColor: theme.palette.common.black,
    borderRadius: 10,
    boxShadow: theme.shadows[5],
  },
  white: {
    backgroundColor: theme.palette.common.white,
  },
  info: {
    padding: theme.spacing(1, 0),
    display: 'flex',
  },
  title: {
    marginRight: theme.spacing(1),
  },
  href: {
    wordBreak: 'break-all',
  },
  type: {
    color: theme.palette.grey[500],
    textTransform: 'capitalize',
  },
  linkIcon: {
    marginRight: theme.spacing(1),
  },
  defaultLink: {
    // textDecoration: 'underline',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.editorPalette.primary.main,
    '&.error': {
      color: theme.editorPalette.text.primary,
    },
  },
  checkBoxRoot: {
    color: 'rgba(200,200,200, 0.54)',
  },
  checkboxLabelLabel: {
    color: theme.palette.grey[500],
    ...theme.typography.body2,
  },
  editLink: {
    marginLeft: theme.spacing(1),
    color: theme.palette.grey[500],
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const stb = string => (string && string !== 'false') || false;

const getLabel = l => l.match(/.+?(?=-link)/)[0];

const DefaultLink = ({ children, item, ...props }) => {
  const classes = useStyles();
  return (
    <a
      className={cx(classes.defaultLink, 'editorDefaultLink', {
        error: !item || item.status !== 200,
      })}
      {...props}
    >
      {children}
    </a>
  );
};

const invalidateEmptyString = s => (!s || s === '' ? null : s);

const InfoWithoutTheme = ({
  data,
  label,
  external,
  href,
  type,
  onToggleExternal,
  onClickEdit,
  onDelete,
}) => {
  const classes = useStyles();
  const i18n = useI18n();
  const adminHrefAndAs =
    type === 'page-link'
      ? { href: `/admin/p/[uuid]`, as: `/admin/p/${data.uuid}` }
      : { href: `/admin/media/[uuid]`, as: `/admin/media/${data.uuid}` };
  return (
    <div className={classes.infoRoot}>
      <div className={classes.info}>
        <MediaIcon
          sourceType={data.sourceType}
          type={type === 'page-link' ? 'page-link' : data.class}
          fontSize="small"
          className={classes.linkIcon}
        />
        <Box>
          <Box mb={1}>
            <Typography variant="body2" className={classes.title}>
              <Box mr={1} component="span" color={`state.${data.state}`}>
                {i18n.t(`PageStateDialog.${data.state}`)}
              </Box>
              <span>|</span>
              <Link {...adminHrefAndAs} passHref>
                <Box
                  target="_blank"
                  component="a"
                  className={classes.editLink}
                  // onClick={onClickEdit}
                >
                  {i18n.t('plugins.links.editLink')}
                </Box>
              </Link>
            </Typography>
            {null && (
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.type}
              >
                {label}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="subtitle1">
              {data.title}{' '}
              {null && type === 'page-link' && (
                <Box mr={1} display="inline-flex">
                  ({i18n.t(`plugins.links.types.${type}`)})
                </Box>
              )}
            </Typography>
            <Typography className={classes.href} variant="body2">
              {href}
            </Typography>
          </Box>
        </Box>
      </div>
      <Box display="flex" justifyContent="space-between">
        <FormControlLabel
          classes={{
            label: classes.checkboxLabelLabel,
          }}
          control={
            <Checkbox
              classes={{
                root: classes.checkBoxRoot,
              }}
              size="small"
              checked={external}
              onChange={onToggleExternal}
            />
          }
          label={i18n.t('plugins.links.openInNewTab')}
        />
        <Button color="default" onClick={onDelete} endIcon={<DeleteIcon />}>
          {i18n.t('plugins.links.deleteLink')}
        </Button>
      </Box>
    </div>
  );
};

const Info = props => (
  <MuiThemeProvider theme={adaTheme}>
    <InfoWithoutTheme {...props} />
  </MuiThemeProvider>
);

const EditLinkContainer = ({ href, onCancel }) => {
  return (
    <MuiThemeProvider theme={adaTheme}>
      <Box color="text.primary" display="flex" flexDirection="column">
        <Box mb={2}>
          <Typography>{href}</Typography>
        </Box>
        <Box display="flex" margin="auto">
          <Box mr={1}>
            <Button size="small" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          </Box>
          <Box ml={1}>
            <Button size="small" variant="contained" color="primary">
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </MuiThemeProvider>
  );
};

const createLinkEntity = (PageLink, MediaLink) => {
  const Link = observer(props => {
    const {
      contentState,
      entityKey,
      children,
      mediaStore,
      pagesStore,
      appStore,
      loginStore,
      blockKey,
      ...restProps
    } = props;
    const classes = useStyles();
    const [item, setItem] = React.useState(null);
    const [edit, setEdit] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const entity = contentState.getEntity(entityKey);
    const type = entity.getType();
    const entityData = entity.getData();
    const isAdmin = loginStore.currentUser.isAdmin;
    const hasData = item && item.data;
    React.useEffect(() => {
      const loadItem = async () => {
        if (!item) {
          if (type === 'media-link') {
            const response = await mediaStore.loadMediaItem(entityData.uuid);
            setItem(response);
          } else if (type === 'page-link') {
            const response = await pagesStore.loadPage(entityData.uuid);
            if (response.status === 200) {
              const item = pagesStore.pages.find(
                p => p.uuid === entityData.uuid,
              );
              setItem({
                data: item,
                status: 200,
              });
            } else {
              setItem({
                data: null,
                status: response.status,
              });
            }
          }
        }
      };
      if (!isLoading) {
        loadItem();
        setIsLoading(true);
      }
    });
    const handlePauseEditor = () => {
      appStore.setPaused(true);
    };
    const handleUnPauseEditor = () => {
      appStore.setPaused(false);
    };
    const handleToggleExternal = () => {
      pagesStore.currentPage.updateEntityData(blockKey, entityKey, {
        openNewTab: !stb(entityData.openNewTab),
      });
    };
    const handleDelete = () => {
      pagesStore.currentPage.deleteEntity(entityKey);
      appStore.setPaused(false);
    };
    const handleClickEdit = () => {
      // setEdit(true);
    };
    const handleClickEditCancel = () => {
      setEdit(false);
    };
    const label = getLabel(type);
    let href = null;
    if (hasData) {
      href = item.data.source;
      if (item.data.source && !item.data.source.includes('http:')) {
        href = `${item.data.source}`;
      }
      if (item.data.source && item.data.source.startsWith('www.')) {
        href = `http://${item.data.source}`;
      }
      if (type === 'page-link') {
        href = `/p/${item.data.uuid}`;
      }
      if (item.data.class === 'file') {
        href = `/ada-assets/${item.data.uuid}`;
      }
    }
    return (
      <Tooltip
        classes={{
          tooltip: cx(classes.tooltip, { [classes.white]: edit }),
        }}
        title={
          hasData &&
          (!edit ? (
            <Info
              label={label}
              data={item.data}
              external={stb(entityData.openNewTab)}
              href={href}
              type={type}
              onToggleExternal={handleToggleExternal}
              onDelete={handleDelete}
              onClickEdit={handleClickEdit}
            />
          ) : (
            <EditLinkContainer href={href} onCancel={handleClickEditCancel} />
          ))
        }
        placement="top"
        interactive
        leaveDelay={250}
        onOpen={handlePauseEditor}
        onClose={handleUnPauseEditor}
        disableFocusListener={!isAdmin || !appStore.editMode}
        disableHoverListener={!isAdmin || !appStore.editMode}
        disableTouchListener={!isAdmin || !appStore.editMode}
      >
        <span>
          <PageLink
            href={href}
            target={
              entityData.openNewTab || appStore.editMode ? '_blank' : '_self'
            }
            item={item}
          >
            {children}
          </PageLink>
        </span>
      </Tooltip>
    );
  });

  return withStores('mediaStore', 'pagesStore', 'appStore', 'loginStore')(Link);
};

const LinkComp = createLinkEntity(DefaultLink, DefaultLink);

const createLinkEntitiesPlugin = (PageLink=DefaultLink, MediaLink=DefaultLink) => { // eslint-disable-line
  return {
    init: pluginMethods => {
      editorMethods.getProps = pluginMethods.getProps;
      editorMethods.setEditorState = pluginMethods.setEditorState;
      editorMethods.setPauseEditor = pluginMethods.setPauseEditor;
    },
    decorators: [
      {
        strategy: (contentBlock, callback, contentState) => {
          contentBlock.findEntityRanges(character => {
            const entityKey = character.getEntity();
            return (
              entityKey !== null &&
              (contentState.getEntity(entityKey).getType() === 'media-link' ||
                contentState.getEntity(entityKey).getType() === 'page-link')
            );
          }, callback);
        },
        component: LinkComp,
      },
    ],
  };
};

export default createLinkEntitiesPlugin;
